import { useEffect, useRef } from 'react';
import { useMomentStore } from '../../stores/moment.store';
import { AnimatedNumber } from './AnimatedNumber';
import { Icon } from './Icon';
import { fmtEur } from '../../lib/utils';

const DURATION = 3200;     // durata totale del momento (invariata)
const SPARK_COUNT = 7;     // scintille che schizzano dal punto d'impatto
const SPARK_DELAY = 340;   // partono col flare (momentFlare in CSS parte a 320ms)
const CARD_DELAY = 350;    // la card coi numeri appare dopo il flare

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Root del sistema "momenti soldi". Montato una volta in AppShell. */
export function MomentRoot() {
  const active = useMomentStore((s) => s.active);
  if (!active) return null;
  return <MomentOverlay />;
}

function MomentOverlay() {
  const payload = useMomentStore((s) => s.payload)!;
  const dismiss = useMomentStore((s) => s.dismiss);
  const sparksRef = useRef<HTMLDivElement>(null);
  const reduce = prefersReducedMotion();

  // Auto-dismiss + Esc
  useEffect(() => {
    const t = setTimeout(dismiss, DURATION);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [dismiss]);

  // Scintille dal punto d'impatto: one-shot via Web Animations API,
  // solo transform/opacity (la lama e il flare vivono interamente in CSS)
  useEffect(() => {
    if (reduce) return;
    const host = sparksRef.current;
    if (!host) return;
    const sparks = Array.from(host.children).filter(
      (el): el is HTMLElement => el instanceof HTMLElement && typeof el.animate === 'function'
    );
    const anims = sparks.map((el, i) => {
      // ventaglio attorno all'impatto, schiacciato sull'asse della lama
      const angle = (i / SPARK_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.6;
      const dist = 70 + Math.random() * 90;
      const dx = Math.cos(angle) * dist;
      const dy = Math.sin(angle) * dist * 0.6;
      return el.animate(
        [
          { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
          { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.2)`, opacity: 0 },
        ],
        {
          duration: 560 + Math.random() * 180,
          delay: SPARK_DELAY,
          easing: 'cubic-bezier(0.16, 0.84, 0.3, 1)',
          fill: 'forwards',
        }
      );
    });
    return () => anims.forEach((a) => a.cancel());
  }, [reduce]);

  const tone = payload.tone ?? 'ok';
  const accent = tone === 'marketing' ? 'var(--brand-marketing)' : tone === 'product' ? 'var(--brand-product)' : 'var(--brand-design)';

  return (
    <div className="moment-overlay moment-scrim-violet" onClick={dismiss} role="dialog" aria-label={payload.title}>
      {/* La lama: si disegna sinistra→destra in 420ms (in reduced-motion resta statica) */}
      <div className="moment-blade" aria-hidden="true" />
      {/* Scintille: invisibili a riposo, mosse dal JS al momento dell'impatto */}
      {!reduce && (
        <div ref={sparksRef} aria-hidden="true">
          {Array.from({ length: SPARK_COUNT }, (_, i) => (
            <span
              key={i}
              className="moment-spark"
              style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)', opacity: 0 }}
            />
          ))}
        </div>
      )}
      <div
        className="moment-card"
        style={{
          ['--moment-accent' as any]: accent,
          // La card appare dopo il flare; in reduced-motion subito (fadeIn da CSS)
          animationDelay: reduce ? undefined : `${CARD_DELAY}ms`,
        }}
      >
        <Icon name="check" draw size={34} className="moment-check" />
        <div className="moment-kicker">{payload.title}</div>
        {payload.amount != null && (
          <div className="moment-amount">
            <span className="u">€</span>
            <AnimatedNumber value={payload.amount} format={(n) => fmtEur(n)} duration={1100} />
          </div>
        )}
        {payload.subtitle && <div className="moment-sub">{payload.subtitle}</div>}
        <div className="moment-hint">clicca per chiudere</div>
      </div>
    </div>
  );
}
